import Button from '@jetbrains/ring-ui/components/button/button'
import ButtonGroup from '@jetbrains/ring-ui/components/button-group/button-group'
import {memo, useCallback, useEffect, useState} from 'react'

import {ChangesType} from '../../../../types'
import {UserProperties} from '../../../../utils/userProperties'
import ArtifactDependenciesChangesList from '../../../common/ArtifactChanges/ArtifactChanges'
import BuildNumber from '../../../common/BuildNumber/BuildNumber.container'
import ContentPanel from '../../../common/ContentPanel/ContentPanel'
import RouterLinkWrapper from '../../../common/RouterLinkWrapper/RouterLinkWrapper'
import {useChangesCountHeading} from '../Changes.hooks'
import ChangesList from '../ChangesList/ChangesList'

import type {Props} from './ChangesPreview.types'
import {getDefaultChangesType} from './ChangesPreview.utils'
import ChangesSinceLastSuccessfulBuild from './ChangesSinceLastSuccessfulBuild/ChangesSinceLastSuccessfulBuild.container'

import styles from './ChangesPreview.css'

function ChangesPreview(props: Props) {
  const {
    className,
    buildId,
    buildTypeId,
    locator,
    changesCount = 0,
    artifactChangesCount = 0,
    baseChangesTabLink,
    firstBuildIdWithSameChanges,
    visibleCount,
    onShowChangeDetails,
    onHideChangeDetails,
    withExpandableLines,
    withBorder,
    withPanel,
    withSinceLastSuccessfulBuild = false,
  } = props
  const count = changesCount + artifactChangesCount
  const [changesType, setChangesType] = useState(getDefaultChangesType(changesCount))
  useEffect(() => setChangesType(getDefaultChangesType(changesCount)), [changesCount])

  const onSelectChangesType = useCallback(
    () => setChangesType(ChangesType.CHANGES),
    [setChangesType],
  )
  const onSelectArtifactsChangesType = useCallback(
    () => setChangesType(ChangesType.ARTIFACTS),
    [setChangesType],
  )

  const renderContent = (localClassName = '') => (
    <div className={localClassName}>
      {firstBuildIdWithSameChanges != null && (
        <div className={styles.firstBuildWithSameChanges}>
          <span>{`The first build with ${
            changesCount > 1 ? 'these changes' : 'this change'
          } was `}</span>
          <BuildNumber buildId={firstBuildIdWithSameChanges} hideStar />
        </div>
      )}
      {changesCount > 0 && artifactChangesCount > 0 && (
        <ButtonGroup className={styles.changesTypeFilter}>
          <Button onClick={onSelectChangesType} active={changesType === ChangesType.CHANGES}>
            <>
              {'VCS changes'}
              <span className={styles.count}>{changesCount}</span>
            </>
          </Button>
          <Button
            onClick={onSelectArtifactsChangesType}
            active={changesType === ChangesType.ARTIFACTS}
          >
            <>
              {'Artifact dependency changes'}
              <span className={styles.count}>{artifactChangesCount}</span>
            </>
          </Button>
        </ButtonGroup>
      )}
      {changesType === ChangesType.CHANGES && (
        <ChangesList
          buildTypeId={buildTypeId}
          locator={locator}
          visibleCount={visibleCount}
          onHideChangeDetails={onHideChangeDetails}
          onShowChangeDetails={onShowChangeDetails}
        />
      )}
      {changesType === ChangesType.ARTIFACTS && buildId != null && (
        <ArtifactDependenciesChangesList
          buildId={buildId}
          visibleCount={visibleCount}
          withExpandableLines={withExpandableLines}
          withSmallPadding
        />
      )}

      <div className={styles.changesButtons}>
        {((changesType === ChangesType.ARTIFACTS && artifactChangesCount > visibleCount) ||
          (changesType === ChangesType.CHANGES && changesCount > visibleCount)) && (
          <RouterLinkWrapper>
            <Button href={`${baseChangesTabLink}&changesType=${changesType}`}>{`Show all`}</Button>
          </RouterLinkWrapper>
        )}

        {withSinceLastSuccessfulBuild && buildId && (
          <ChangesSinceLastSuccessfulBuild
            buildTypeId={buildTypeId}
            buildId={buildId}
            changesCount={changesCount}
          />
        )}
      </div>
    </div>
  )

  const heading = useChangesCountHeading(changesCount, artifactChangesCount)

  if (count === 0) {
    return null
  }

  if (withPanel !== true) {
    return renderContent(className)
  }

  return (
    <ContentPanel
      className={className}
      content={renderContent()}
      heading={heading}
      href={baseChangesTabLink}
      panelType={UserProperties.EXPAND_BUILD_CHANGES_SECTION}
      withBorder={withBorder === true}
    />
  )
}

export default memo<Props>(ChangesPreview)
